import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg, publicImg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { allPaths } from "../../core/menu";
import { MONTHS, fa, formatDate, formatLongDate, initials, isoToJ } from "../../core/format";
import PortScene from "../../components/PortScene";
import { StatusBadge } from "../../components/Table";
import ResponsiveList from "../../components/ResponsiveList";
import { TaskReportModal } from "../work/Tasks";
import { NewsModal } from "../../components/AnnouncementFiles";
/* ---------- بانک شعار ایمنی (هر روز یکی) ---------- */
const SLOGANS = [
  "ایمنی، پایه‌ی توسعه پایدار بندر است.",
  "ایمنی یک انتخاب نیست؛ یک وظیفه است.",
  "امروز با ایمنی کار کنید تا فردا سالم برگردید.",
  "هر حادثه‌ای پیشگیری‌پذیر است.",
  "ایمنی، وظیفه ماست.",
  "یک لحظه بی‌احتیاطی، یک عمر پشیمانی.",
  "ایمنی از من شروع می‌شود.",
  "کار ایمن، کار با کیفیت است.",
  "تجهیزات سالم، بندر امن.",
  "پیشگیری همیشه ارزان‌تر از جبران است.",
  "قبل از شروع کار، ایمنی را بررسی کنید.",
  "کلاه ایمنی، کوچک‌ترین هزینه برای بزرگ‌ترین حفاظت.",
  "ایمنی را به همکارانتان یادآوری کنید.",
  "حادثه خبر نمی‌کند؛ آمادگی لازم است.",
  "دریا قانون دارد؛ ایمنی هم.",
  "در بندر، ایمنی اولین بار است نه آخرین.",
  "آتش را از جرقه‌ی اول جدی بگیرید.",
  "همکاری و مسئولیت، ضامن امنیت بندر.",
  "ایمنی یعنی بازگشت سالم به آغوش خانواده.",
  "کپسول شارژ‌شده، بندر مطمئن.",
  "خطر را گزارش کنید، قبل از آنکه حادثه شود.",
  "دانش ایمنی، بهترین سپر است.",
  "عجله، دشمن ایمنی است.",
  "نظم و دقت، دو بال ایمنی‌اند.",
  "ایمنی هزینه نیست، سرمایه‌گذاری است.",
  "هر روز کاری، فرصتی برای ایمن‌تر شدن.",
  "آتش‌نشان بودن، یعنی همیشه آماده بودن.",
  "با ایمنی کار کنید، با آرامش زندگی کنید.",
  "تعهد به ایمنی، تعهد به انسان است.",
  "بندر ایمن، مسافر و کارگر آسوده.",
];
const pickSlogan = (iso) => {
  const n = Math.floor(Date.parse(`${iso}T00:00:00Z`) / 86400000) || 0;
  const h = Math.imul(n ^ (n >>> 7), 2654435761) >>> 0;
  return SLOGANS[h % SLOGANS.length];
};

const longDate = (iso) => {
  const j = isoToJ(iso);
  return `${fa(j.jd)} ${MONTHS[j.jm - 1]} ${fa(j.jy)}`;
};

/* ---------- کاشی‌ها (بعد از اخبار) ---------- */
const TILES = [
  { key: "directives", title: "ابلاغ‌های شیف آتش نشانی", icon: "fa-solid fa-bullhorn", color: "#F59E0B", to: "/directives",
    n: (c) => c.directives, small: (c, role) => (role === "manager" ? "ابلاغ باز" : "در انتظار انجام") },
  { key: "requests", title: "درخواست‌ها", icon: "fa-solid fa-file-lines", color: "#2563EB", to: "/requests/personal",
    n: (c) => c.requests, small: () => "در انتظار بررسی" },
  { key: "pledges", title: "تعهدنامه‌ها", icon: "fa-solid fa-clipboard-check", color: "#16A34A", to: "/pledges/repair",
    n: (c) => c.pledges, small: () => "در ۳۰ روز گذشته" },
  { key: "incidents", title: "حوادث", icon: "fa-solid fa-fire", color: "#E63946", to: "/incidents",
    n: (c) => c.incidents, small: () => "در ۳۰ روز گذشته" },
  { key: "letters", title: "نامه‌ها", icon: "fa-regular fa-envelope-open", color: "#8B5CF6", to: "/manager-letters",
    n: (c) => c.letters, small: () => "نامه‌ی خوانده‌نشده" },
  { key: "lettersAdmin", title: "نامه‌نگاری", icon: "fa-regular fa-envelope", color: "#0D8AC9", to: "/letters", roles: ["admin"],
    n: (c) => c.lettersAdmin, small: () => "در ۳۰ روز گذشته" },
  { key: "equipment", title: "تجهیزات", icon: "fa-solid fa-fire-extinguisher", color: "#DC2626", to: "/equipment",
    n: (c) => c.equipment, small: (c) => (c.expiring ? `${fa(c.expiring)} مورد در حال انقضا` : "همه‌ی موارد معتبر") },
  { key: "leave", title: "تأیید مرخصی", icon: "fa-solid fa-user-check", color: "#0D3B66", to: "/leave-approvals",
    n: (c) => c.leave, small: () => "در انتظار تأیید شما" },
  { key: "assigned", title: "وظایف محوله", icon: "fa-solid fa-clipboard-list", color: "#0F766E", to: "/assigned-tasks",
    n: (c) => c.assigned?.total || 0,
    small: (c) => `${fa(c.assigned?.missed || 0)} انجام‌نشده · ${fa(c.assigned?.done || 0)} انجام‌شده` },
];

const QUICK = [
  // { title: "وظایف روزانه", icon: "fa-solid fa-list-check", color: "#F59E0B", to: "/tasks" },
  // { title: "تعهدنامه‌ها", icon: "fa-solid fa-file-signature", color: "#16A34A", to: "/pledges/repair" },
  // { title: "صدور ماموریت", icon: "fa-solid fa-paper-plane", color: "#0D8AC9", to: "/missions" },
  // { title: "ثبت درخواست", icon: "fa-solid fa-clipboard-list", color: "#8B5CF6", to: "/requests/personal" },
  // { title: "ثبت حادثه", icon: "fa-solid fa-fire", color: "#E63946", to: "/incidents" },
];

const CSS = `
.hb{position:relative;overflow:hidden;border-radius:16px;min-height:200px;padding:20px 24px;color:#fff;display:flex;flex-direction:column;justify-content:flex-end;gap:8px;border:1px solid var(--border);box-shadow:var(--shadow-sm);background-size:cover;background-position:center;background-color:#0D3B66}
.hb svg{position:absolute;inset:0;width:100%;height:100%}
.hb::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(8,28,50,.2),rgba(8,28,50,.88))}
.hb>*{position:relative;z-index:1}
.hb-date{align-self:flex-start;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.4);border-radius:999px;padding:3px 14px;font-weight:700;font-size:13px}
.hb-slogan{font-size:18px;font-weight:800;line-height:1.9}
.eq-alerts{display:grid;gap:8px}
.eq-alert{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:12px;font-size:13.5px;font-weight:600;text-decoration:none;border:1.5px solid}
.eq-alert.bad{background:var(--danger-soft);color:#b3202c;border-color:var(--danger)}
.eq-alert.warn{background:var(--warn-soft);color:#b45309;border-color:var(--warn)}
.eq-alert i{flex:none}
.eq-alert span{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.eq-more{justify-self:start}
.sp{display:flex;flex-wrap:wrap;gap:18px;margin-top:8px}
.sp-i{display:flex;flex-direction:column;align-items:center;gap:6px;width:92px;text-align:center}
.sp-av{width:68px;height:68px;border-radius:50%;object-fit:cover;background:var(--primary-soft);color:var(--primary);display:grid;place-items:center;font-weight:800;font-size:19px;border:2px solid var(--border)}
.sp-n{font-size:12.5px;font-weight:700;line-height:1.6}
.news-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:0 18px}
.task-link{background:none;border:none;padding:0;font:inherit;font-weight:800;color:var(--primary);cursor:pointer;text-align:right}
.task-link:hover{text-decoration:underline}
@media (max-width:640px){
  .hb-slogan{font-size:15px}
  .eq-alert span{white-space:normal}
  .hb{min-height:160px}
}

.news-btn{background:none;border:none;border-bottom:1px solid var(--border);text-align:right;cursor:pointer;font:inherit;color:inherit;width:100%}
.news-btn:hover b{color:var(--primary);text-decoration:underline}
`;

export default function Home() {
  const { user } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [showAllEq, setShowAllEq] = useState(false);
  const [viewTask, setViewTask] = useState(null);
const [newsId, setNewsId] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);

  // همه‌ی شیفت‌ها ۲۴ ساعته‌اند؛ افراد همه‌ی انواع در یک فهرست جمع می‌شوند
  const people = useMemo(() => {
    const seen = new Set();
    const out = [];
    (d?.shifts || []).forEach((s) => {
      const list = s.people || (s.names || []).map((name) => ({ name, photo: null }));
      list.forEach((p) => {
        if (!seen.has(p.name)) { seen.add(p.name); out.push(p); }
      });
    });
    return out;
  }, [d]);

  if (err) return <div className="alert err">{err}</div>;
  if (!d) return <div className="empty"><div className="spinner" style={{ margin: "0 auto" }} /></div>;

  const paths = allPaths(user.role);
  const can = (to) => paths.includes(to);
  const counts = d.counts || {};
  const tiles = TILES.filter((t) => (!t.roles || t.roles.includes(user.role)) && can(t.to));
  const quick = QUICK.filter((q) => can(q.to));
  const expiring = d.expiring || [];
  const shownEq = showAllEq ? expiring : expiring.slice(0, 5);

  return (
    <div className="home-main">
      <style>{CSS}</style>

      {/* شعار روز + تاریخ */}
   <div className="hb" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/home-hero.jpg)` }}>

        {/* <PortScene /> */}
        <span className="hb-date"><i className="fa-regular fa-calendar" style={{ marginInlineEnd: 6 }} />{formatLongDate(d.today)}</span>
        <div className="hb-slogan">“{pickSlogan(d.today)}”</div>
      </div>

      {/* هشدار تجهیزات در حال اتمام / تمام‌شده */}
      {expiring.length > 0 && (
        <div className="eq-alerts">
          {shownEq.map((e) => {
            const text = `${e.name}${e.code ? ` با شماره انبار ${e.code}` : ""}${e.location ? ` در محل ${e.location}` : ""} در تاریخ ${longDate(e.expiryDate)} ${e.expired ? "به اتمام رسیده است." : "به اتمام خواهد رسید."}`;
            return (
              <Link key={e.id} to="/equipment" className={`eq-alert ${e.expired ? "bad" : "warn"}`} title={text}>
                <i className={`fa-solid ${e.expired ? "fa-circle-exclamation" : "fa-triangle-exclamation"}`} />
                <span>{text}</span>
              </Link>
            );
          })}
          {expiring.length > 5 && (
            <button className="btn btn-sm btn-ghost eq-more" onClick={() => setShowAllEq((v) => !v)}>
              {showAllEq ? "نمایش کمتر" : `نمایش همه‌ی ${fa(expiring.length)} مورد`}
            </button>
          )}
        </div>
      )}

      {/* آخرین اخبار و اطلاعیه‌ها */}
      <div className="card box">
        <div className="box-head">
          <h3><i className="fa-regular fa-newspaper" /> آخرین اخبار و اطلاعیه‌ها</h3>
          {user.role === "admin" && <Link className="link-more" to="/settings">مدیریت</Link>}
        </div>
        {d.news.length === 0 && <div className="hint">اطلاعیه‌ای ثبت نشده است.</div>}
       <div className="news-grid">
  {d.news.map((n) => (
    <button key={n.id} type="button" className="news-i news-btn" onClick={() => setNewsId(n.id)}>
      <b>{n.title}</b>
      <small>{formatDate(n.publishDate)} · برای مشاهده کلیک کنید</small>
    </button>
  ))}
</div>
      </div>

      {/* کاشی‌ها */}
      {tiles.length > 0 && (
        <div className="stats">
          {tiles.map((t) => (
            <Link to={t.to} key={t.key} className="card stat">
              <div className="ic" style={{ color: t.color }}><i className={t.icon} /></div>
              <span>{t.title}</span>
              <b>{fa(t.n(counts) || 0)}</b>
              <small>{t.small(counts, user.role)}</small>
            </Link>
          ))}
        </div>
      )}

      <div className="two">
        {/* شیفت امروز */}
        <div className="card box">
          <div className="box-head">
            <h3>
              شیفت کاری امروز
              <span className="chip">{formatLongDate(d.today)}</span>
            </h3>
          </div>
          {people.length === 0 ? (
            <div className="hint">امروز کسی در شیفت نیست.</div>
          ) : (
            <div className="sp">
              {people.map((p) => (
                <div className="sp-i" key={p.name}>
                  {p.photo ? (
                    <img className="sp-av" src={publicImg("employees", p.photo)} alt={p.name} />
                  ) : (
                    <span className="sp-av">{initials(p.name)}</span>
                  )}
                  <span className="sp-n">{p.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* وظایف امروز */}
        <div className="card box">
          <div className="box-head">
            <h3><i className="fa-solid fa-list-check" /> وظایف امروز</h3>
            {can("/tasks") && <Link className="link-more" to="/tasks">مشاهده همه</Link>}
          </div>
          {d.tasks.length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>برای امروز وظیفه‌ای ثبت نشده است.</div>
          ) : (
            <ResponsiveList
              rows={d.tasks}
              loading={false}
              empty="برای امروز وظیفه‌ای ثبت نشده است."
              onRowClick={(r) => setViewTask(r)}
              columns={[
                ...(user.role !== "staff" ? [{ key: "employeeName", label: "نام کارمند" }] : []),
                {
                  key: "title", label: "عنوان وظیفه",
                  render: (r) => <button className="task-link" onClick={() => setViewTask(r)}>{r.title}</button>,
                },
                { key: "taskDate", label: "تاریخ", render: (r) => formatDate(r.taskDate) },
                { key: "status", label: "وضعیت", render: (r) => <StatusBadge status={r.status} label={r.statusLabel} /> },
              ]}
              actions={(r) => (
                <button className="icon-btn" title="مشاهده جزئیات" onClick={() => setViewTask(r)}>
                  <i className="fa-regular fa-eye" />
                </button>
              )}
              mobileTitle={(r) => r.title}
              mobileBadge={(r) => <StatusBadge status={r.status} label={r.statusLabel} />}
              mobileMeta={(r) => [
                ...(user.role !== "staff" ? [{ icon: "fa-solid fa-user", label: "کارمند", value: r.employeeName }] : []),
                { icon: "fa-regular fa-calendar", label: "تاریخ", value: formatDate(r.taskDate) },
              ]}
            />
          )}
        </div>
      </div>

      {quick.length > 0 && (
        <div className="quick">
          {quick.map((q) => (
            <Link key={q.title} to={q.to}><i className={q.icon} style={{ color: q.color }} />{q.title}</Link>
          ))}
        </div>
      )}

    <div className="banner" style={{ backgroundImage: `linear-gradient(100deg, rgba(10,42,74,.88), rgba(13,59,102,.65)), url(${process.env.PUBLIC_URL}/home-footer.jpg)`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div>
          <h3>ایمنی امروز، آرامش فردا</h3>
          <p>همکاری، مسئولیت و تعهد، ضامن امنیت بندر</p>
        </div>
        <div className="shield"><i className="fa-solid fa-shield-halved" /></div>
      </div>

      {viewTask && (
        <TaskReportModal task={viewTask} mode="view" onClose={() => setViewTask(null)} onDone={() => setViewTask(null)} />
      )}


      {newsId && <NewsModal id={newsId} onClose={() => setNewsId(null)} />}
    </div>
  );
}